export const state = {
    branch_orders: [],
    order_statuses: [],
    order_properties: [],
    selected_order_id: -1,
    selected_order_details: null,
    statsOverview: null,

    ext_order: null, //* order loaded via token
    is_order_readonly: false,

    branch_orders_loading_status: null,
};

export const getters = {};

export const actions = {
    //* PRODUCTS
    getBranchOrders({ commit }, branch_id) {
        return new Promise((resolve, reject) => {
            commit("setBranchOrdersLoadingStatus", true);
            window.axios
                .get("api/orders/all", { params: { branch_id: branch_id } })
                .then((response) => {
                    commit("setOrdersData", response.data);
                    commit("setBranchOrdersLoadingStatus", false);
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });
    },
    getOrderStatuses({ commit }) {
        return new Promise((resolve, reject) => {
            window.axios
                .get("api/orders/statuses")
                .then((response) => {
                    commit("setOrderStatuses", response.data);
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });
    },
    getOrderProperties({ commit }) {
        return new Promise((resolve, reject) => {
            window.axios
                .get("api/orders/properties")
                .then((response) => {
                    commit("setOrderPropertiesData", response.data);
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });
    },
    // eslint-disable-next-line
    getOrderDetails({ commit, state }, url = state.selected_order_id) {
        return new Promise((resolve, reject) => {
            window.axios
                .get("api/orders/order", {
                    params: { id: url },
                })
                .then((response) => {
                    commit("setOrderDetails", response.data);
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });
    },
    // Story 6.3: print-time server re-eval of the order's bulk line items. Returns
    // { has_bulk, items:[{client_name, product_name}] } straight to the caller — no
    // commit (the result is transient, used to build printTag jobs, not stored).
    // eslint-disable-next-line no-unused-vars, no-empty-pattern
    getBulkLabelItems({}, id) {
        return new Promise((resolve, reject) => {
            window.axios
                .get("api/orders/bulklabelitems", {
                    params: { id },
                })
                .then((response) => {
                    resolve(response.data);
                })
                .catch(() => {
                    reject();
                });
        });
    },
    // Story 7.3: ship the order via BobGo and commit the returned (decorated) Order so the
    // screen shows the waybill + tracking links immediately. Unlike the other actions in
    // this module, the failure branch must NOT swallow the body — DispatchAction needs the
    // server's { message, retryable } to show the real error + Retry. retryable defaults to
    // true unless the server explicitly says false.
    shipViaBobGo({ commit }, id) {
        return new Promise((resolve, reject) => {
            window.axios
                .post("api/orders/shipviabobgo", { id })
                .then((response) => {
                    commit("setOrderDetails", response.data);
                    resolve(response.data);
                })
                .catch((err) => {
                    reject({
                        message: err.response?.data?.message || "Ship failed.",
                        retryable: err.response?.data?.retryable !== false,
                    });
                });
        });
    },
    // Story 7.8: push the order's invoice to Sage and commit the returned (bare) Order so the
    // panel shows the Invoiced badge + sage_invoice_ref immediately. Exact mirror of
    // shipViaBobGo — same deliberate exception to the module's silent .catch: DispatchAction
    // needs the server's { message, retryable } to render the real error + Retry (the 7.7
    // contract guarantees this shape, incl. 429 → "Sage busy — retry shortly." retryable:true).
    pushToSage({ commit }, id) {
        return new Promise((resolve, reject) => {
            window.axios
                .post("api/orders/pushtosage", { id })
                .then((response) => {
                    commit("setOrderDetails", response.data);
                    resolve(response.data);
                })
                .catch((err) => {
                    reject({
                        message: err.response?.data?.message || "Invoice push failed.",
                        retryable: err.response?.data?.retryable !== false,
                    });
                });
        });
    },
    // eslint-disable-next-line
    createOrder({}, requestObject) {
        return new Promise((resolve, reject) => {
            window.axios
                .post("api/orders/create", requestObject)
                .then((response) => {
                    resolve(response.data);
                })
                .catch(() => {
                    reject();
                });
        });
    },
    // eslint-disable-next-line
    deleteOrder({}, requestObject) {
        return new Promise((resolve, reject) => {
            window.axios
                .post("api/orders/delete", requestObject)
                .then((response) => {
                    resolve(response.data);
                })
                .catch(() => {
                    reject();
                });
        });
    },
    setSelectedOrder({ commit }, order_id) {
        commit("setSelectedOrder", order_id);
    },

    //* External order page
    getTokenOrderDetails({ commit }, token) {
        return new Promise((resolve, reject) => {
            window.axios
                .get("api/orders/orderbytoken", { params: { token: token } })
                .then((response) => {
                    commit("setTokenOrder", response.data);
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });
    },

    // eslint-disable-next-line
    updateOrder({}, requestObject) {
        return new Promise((resolve, reject) => {
            window.axios
                .post("api/orders/update", requestObject)
                .then((response) => {
                    resolve(response.data);
                })
                .catch(() => {
                    reject();
                });
        });
    },
    // Duplicate
    // eslint-disable-next-line
    // updateOrder({}, requestObject) {
    //     return new Promise((resolve, reject) => {
    //         window.axios
    //             .post("api/orders/update", requestObject)
    //             .then((response) => {
    //                 resolve(response.data);
    //             })
    //             .catch(() => {
    //                 reject();
    //             });
    //     });
    // },

    // eslint-disable-next-line no-unused-vars
    updateExternalOrderStatus({ commit }, requestObject) {
        return new Promise((resolve, reject) => {
            window.axios
                .post("api/orders/updatestatus", requestObject)
                .then((response) => {
                    resolve(response.data);
                })
                .catch(() => {
                    reject();
                });
        });
    },
};

export const mutations = {
    setOrdersData(state, payload) {
        const { stats, orders } = payload;

        state.branch_orders = orders;
        state.statsOverview = stats;
    },
    setOrderDetails(state, payload) {
        state.is_order_readonly = false;
        state.selected_order_details = payload;
    },
    setTokenOrder(state, payload) {
        state.is_order_readonly = true;
        //* Check if client type is admin to allow the external order to be updated
        if (payload.client.type == "admin") {
            state.is_order_readonly = false;
        }
        state.ext_order = payload;
    },
    setSelectedOrder(state, payload) {
        state.selected_order_id = payload;
    },
    setOrderPropertiesData(state, payload) {
        state.order_properties = payload;
    },
    setBranchOrdersLoadingStatus(state, payload) {
        state.branch_orders_loading_status = payload;
    },
    setOrderStatuses(state, payload) {
        state.order_statuses = payload;
    }
};
